import React, { useState } from 'react';
import {
    Box, Typography, TextField, Button, CircularProgress, Snackbar, Alert
} from '@mui/material';
import { motion } from 'framer-motion';
import { BASE_URL } from '../../utils/endpoints';


const containerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.5,
            ease: "easeOut",
            staggerChildren: 0.1,
        },
    },
    exit: {
        opacity: 0,
        y: -50,
        transition: { duration: 0.3, ease: "easeIn" },
    },
};


const fieldVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
        opacity: 1,
        x: 0,
        transition: { duration: 0.4, ease: "easeOut" },
    },
};


const buttonVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
        opacity: 1,
        scale: 1,
        transition: { duration: 0.4, ease: "easeOut" },
    },
    hover: {
        scale: 1.05,
        transition: { duration: 0.2 },
    },
};

const CategoryCreate = ({ onCategoryCreated, handleClose }) => {
    
    const [newCategoryName, setNewCategoryName] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');

    
    const handleAddCategory = async (event) => {
        event.preventDefault();
        setLoading(true);
        setError(null);
        setOpenSnackbar(false);

        if (!newCategoryName?.trim()) {
            setError('Category name cannot be empty');
            setSnackbarMessage('Category name cannot be empty');
            setSnackbarSeverity('error');
            setOpenSnackbar(true);
            setLoading(false);
            return;
        }

        try {
            const response = await fetch(`${BASE_URL}/categories`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    name: newCategoryName,
                }),
            });

            if (response.ok) {
                const newCategory = await response.json();
                setSnackbarMessage(`Category "${newCategoryName}" added successfully!`);
                setSnackbarSeverity('success');
                setOpenSnackbar(true);
                setNewCategoryName(''); 
                onCategoryCreated(); 
                setTimeout(() => {
                    handleClose(); 
                }, 1000);
            } else {
                const errorMessage = 'Failed to add category';
                setError(errorMessage);
                setSnackbarMessage(errorMessage);
                setSnackbarSeverity('error');
                setOpenSnackbar(true);
            }
        } catch (err) {
            const errorMessage = 'Error occurred while adding category';
            setError(errorMessage);
            setSnackbarMessage(errorMessage);
            setSnackbarSeverity('error');
            setOpenSnackbar(true);
        } finally {
            setLoading(false);
        }
    };

    
    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setOpenSnackbar(false);
    };

    return (
        <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
        >
            <Box
                component="form"
                onSubmit={handleAddCategory}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    width: '100%',
                }}
            >
                <motion.div variants={fieldVariants}>
                    <TextField
                        label="Category Name"
                        value={newCategoryName}
                        onChange={(e) => setNewCategoryName(e.target.value)}
                        fullWidth
                        variant="outlined"
                        required
                        disabled={loading}
                        InputLabelProps={{
                            style: { color: '#2e7d32' },
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': { borderColor: '#2e7d32' },
                                '&:hover fieldset': { borderColor: '#2e7d32' },
                                '&.Mui-focused fieldset': { borderColor: '#2e7d32' },
                            },
                        }}
                    />
                </motion.div>

                {error && (
                    <motion.div variants={fieldVariants}>
                        <Typography color="error" variant="body2">
                            {error}
                        </Typography>
                    </motion.div>
                )}

                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                    <motion.div variants={buttonVariants} whileHover="hover">
                        <Button
                            variant="outlined"
                            onClick={handleClose}
                            disabled={loading}
                            sx={{ minWidth: 100, color: '#2e7d32', borderColor: '#2e7d32' }}
                        >
                            Cancel
                        </Button>
                    </motion.div>
                    <motion.div variants={buttonVariants} whileHover="hover">
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={loading}
                            sx={{ minWidth: 100, backgroundColor: '#2e7d32', '&:hover': { backgroundColor: '#45a049' } }}
                        >
                            {loading ? <CircularProgress size={24} /> : 'Add Category'}
                        </Button>
                    </motion.div>
                </Box>
            </Box>

            <Snackbar
                open={openSnackbar}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbarSeverity}
                    sx={{ width: '100%' }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </motion.div>
    );
};

export default CategoryCreate;